package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import kotlinx.coroutines.launch
import com.routexk.quiz.di.LocalAppContainer
import com.routexk.quiz.funnel.FunnelApi
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelPlan
import com.routexk.quiz.funnel.FunnelStore
import com.routexk.quiz.funnel.SelectedPlan
import com.routexk.quiz.funnel.asMoney
import com.routexk.quiz.funnel.ui.FunnelCard
import com.routexk.quiz.funnel.ui.FunnelCta
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.funnel.ui.GuaranteeBadge
import com.routexk.quiz.funnel.ui.MoneyCard
import com.routexk.quiz.funnel.ui.PlanDisplay
import com.routexk.quiz.funnel.ui.PlanSelector
import com.routexk.quiz.funnel.ui.PotentialBars
import com.routexk.quiz.funnel.ui.PotentialItem
import com.routexk.quiz.funnel.ui.StatusBadge
import com.routexk.quiz.funnel.ui.StickyCtaBar
import com.routexk.quiz.funnel.ui.TestimonialCard
import com.routexk.quiz.funnel.ui.TimerBanner
import com.routexk.quiz.funnel.ui.rememberCountdown
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.ui.utils.isMobile
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

private const val DISCOUNT_WINDOW_MS = 10 * 60 * 1000.0

/**
 * Fallback catalog matching the design when the backend is unreachable. Each
 * plan is a paid trial of a different length that renews into the main
 * $49.99/month subscription: 2 weeks $9.99 / 1 month $17.99 / 1 year $159.99.
 */
private val FALLBACK_PLANS = listOf(
    FunnelPlan(uuid = "fallback-2w", name = "2 Week Plan", amountCents = 999, currency = "usd", trialDays = 14),
    FunnelPlan(uuid = "fallback-1m", name = "1 Month Plan", amountCents = 1799, currency = "usd", trialDays = 30),
    FunnelPlan(uuid = "fallback-1y", name = "1 Year Plan", amountCents = 15999, currency = "usd", trialDays = 365),
)

/**
 * Fixed marketing numbers per package length: struck-through original price,
 * advertised weekly rate and the discount badge. Keyed by trialDays so they
 * apply to backend products and the fallback catalog alike.
 */
private data class PlanPricing(
    val discountPct: Int,
    val originalCents: Long,
    val perWeekCents: Long,
    val popular: Boolean = false,
)

private val PRICING_BY_TRIAL_DAYS = mapOf(
    14 to PlanPricing(discountPct = 50, originalCents = 1999, perWeekCents = 499),
    30 to PlanPricing(discountPct = 50, originalCents = 3699, perWeekCents = 459, popular = true),
    365 to PlanPricing(discountPct = 60, originalCents = 39999, perWeekCents = 359),
)

private fun toDisplays(plans: List<FunnelPlan>): List<PlanDisplay> {
    val sorted = plans.sortedBy { it.amountCents }
    val defaultTrialDays = listOf(14, 30, 365)
    return sorted.mapIndexed { index, plan ->
        val trialDays = plan.trialDays ?: defaultTrialDays.getOrElse(index) { 14 }
        val pricing = PRICING_BY_TRIAL_DAYS[trialDays]
        PlanDisplay(
            plan = plan,
            trialDays = trialDays,
            discountPct = pricing?.discountPct ?: 50,
            originalCentsOverride = pricing?.originalCents,
            perWeekCentsOverride = pricing?.perWeekCents,
            popular = pricing?.popular ?: false,
        )
    }
}

@Page("/plan")
@Composable
fun PlanPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()
    val mobile = isMobile()
    val estimate = remember { FunnelStore.estimate }

    LaunchedEffect(Unit) {
        if (!FunnelStore.answers.quizComplete) ctx.router.navigateTo("/quiz")
    }

    val deadline = remember {
        FunnelStore.discountDeadline?.takeIf { it > kotlin.js.Date.now() }
            ?: (kotlin.js.Date.now() + DISCOUNT_WINDOW_MS).also { FunnelStore.discountDeadline = it }
    }
    val secondsLeft by rememberCountdown(deadline)

    var displays by remember { mutableStateOf(toDisplays(FALLBACK_PLANS)) }
    var selectedUuid by remember { mutableStateOf(FunnelStore.selectedPlan?.uuid) }
    var usingFallback by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        Ga4.screenView("paywall", 7, "discount_pct" to 60)
        FbPixel.pageView()
        FbPixel.track(
            "ViewContent",
            params = arrayOf(
                "content_name" to "paywall",
                "content_type" to "product_group",
            ),
        )
        FunnelApi.trackEvent(container.defaultHttpClient, "paywall_view")
        val fetched = FunnelApi.fetchPlans(container.defaultHttpClient)
        if (fetched.isNotEmpty()) {
            displays = toDisplays(fetched)
            usingFallback = false
        }
        Ga4.event(
            "view_item_list",
            "item_list_name" to "paywall_plans",
            "items" to Ga4.itemsArray(displays.map {
                Ga4.planItem(Ga4.planId(it.trialDays), it.label, it.plan.amountCents)
            }),
        )
        if (selectedUuid == null || displays.none { it.plan.uuid == selectedUuid }) {
            // Pre-select the "most popular" plan (falls back to the middle one)
            val popular = displays.firstOrNull { it.popular } ?: displays.getOrNull(1) ?: displays.firstOrNull()
            popular?.let { select(it, track = false) { u -> selectedUuid = u } }
        }
    }

    // Fire once when the 10-minute discount countdown runs out
    var timerExpiredTracked by remember { mutableStateOf(false) }
    LaunchedEffect(secondsLeft) {
        if (secondsLeft <= 0 && !timerExpiredTracked) {
            timerExpiredTracked = true
            val selected = displays.firstOrNull { it.plan.uuid == selectedUuid }
            Ga4.event(
                "discount_timer_expired",
                "screen_name" to "paywall",
                "plan_id" to selected?.let { Ga4.planId(it.trialDays) },
            )
            FbPixel.trackCustom(
                "DiscountExpired",
                "screen_name" to "paywall",
                "plan_id" to selected?.let { Ga4.planId(it.trialDays) },
            )
        }
    }

    // Desktop design starts with the timer banner — no logo (mobile keeps it)
    FunnelLayout(title = com.routexk.quiz.i18n.t("RouteXK — Choose Your Plan", "RouteXK — Elige tu plan"), maxContentWidth = 900.rpx, showLogo = mobile) {
        // Tighter vertical rhythm on mobile so the first plan row fits above the fold
        Column(
            Modifier.fillMaxWidth().gap(if (mobile) 12.rpx else 24.rpx),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            TimerBanner(secondsLeft)

            // Desktop only: everything between the timer and the sticky CTA is
            // rendered at 70% size (the timer banner and the activate CTA keep
            // their full size); mobile keeps the original scale.
            Column(
                Modifier
                    .gap(if (mobile) 12.rpx else 24.rpx)
                    .styleModifier {
                        if (!mobile) property("zoom", "0.7")
                    },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                H1(
                    attrs = Modifier
                        .margin(0.rpx)
                        .fontSize(if (mobile) 26.rpx else 34.rpx)
                        .fontWeight(FontWeight.Bold)
                        .fillMaxWidth()
                        .textAlign(if (mobile) TextAlign.Start else TextAlign.Center)
                        .lineHeight(1.25)
                        .fontFamily(AppTypography.SECONDARY_FONT_NAME, AppTypography.SECONDARY_FONT_FALLBACK)
                        .toAttrs()
                ) {
                    Text(FunnelCopy.PAYWALL_TITLE)
                }

                Row(
                    Modifier.fillMaxWidth().gap(20.rpx).flexWrap(FlexWrap.Wrap),
                    horizontalArrangement = Arrangement.Center
                ) {
                    PotentialBars(
                        items = listOf(
                            PotentialItem(FunnelCopy.POTENTIAL_TIME, FunnelCopy.POTENTIAL_HIGH, estimate.timeSavingPotentialPct, "/funnel/clock-icon.svg"),
                            PotentialItem(FunnelCopy.POTENTIAL_FUEL, FunnelCopy.POTENTIAL_MEDIUM, estimate.fuelSavingPotentialPct, "/funnel/fuelpump-icon.svg"),
                            PotentialItem(
                                if (mobile) FunnelCopy.POTENTIAL_MONEY_SHORT else FunnelCopy.POTENTIAL_MONEY,
                                FunnelCopy.POTENTIAL_EXTREMELY_HIGH,
                                estimate.moneyIncreasePotentialPct,
                                "/funnel/dollar-icon.svg"
                            ),
                        ),
                        modifier = Modifier.flexGrow(1).minWidth(320.rpx).maxWidth(520.rpx)
                            .alignSelf(com.varabyte.kobweb.compose.css.AlignSelf.Stretch),
                        compact = mobile
                    )
                    if (!mobile) MoneyCard(
                        title = FunnelCopy.ESTIMATE_CARD_TITLE,
                        value = "+ " + estimate.moneyIncreaseYearUsd.asMoney(),
                        pill = null,
                        note = FunnelCopy.ESTIMATE_CARD_NOTE,
                        modifier = Modifier.minWidth(300.rpx).maxWidth(360.rpx).flexGrow(1)
                            .alignSelf(com.varabyte.kobweb.compose.css.AlignSelf.Stretch)
                    )
                }

                val claimedPill: @Composable () -> Unit = {
                    Row(
                        Modifier
                            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE8, 0xF6, 0xEB))
                            .border(1.rpx, org.jetbrains.compose.web.css.LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xCD, 0xEB, 0xD3))
                            .borderRadius(14.rpx)
                            .padding(topBottom = 12.rpx, leftRight = 16.rpx)
                            .gap(8.rpx),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        org.jetbrains.compose.web.dom.Img(
                            src = "/funnel/check-circle-green.svg",
                            attrs = { attr("aria-hidden", "true"); style { width(18.rpx); height(18.rpx) } }
                        )
                        SpanText(
                            FunnelCopy.CLAIMED_BADGE.replace("%s", estimate.taxDeductionUsd.asMoney()),
                            Modifier.fontSize(14.rpx).lineHeight(16.rpx).fontWeight(FontWeight.Medium)
                                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x22, 0x52, 0x22))
                        )
                    }
                }

                if (mobile) {
                    // Mobile design: standalone pill, plan rows without a wrapping card
                    claimedPill()
                    PlanSelector(
                        plans = displays,
                        selectedUuid = selectedUuid,
                        onSelect = { display -> select(display) { selectedUuid = it } },
                        modifier = Modifier.fillMaxWidth(),
                        compact = true
                    )
                    GuaranteeBadge(Modifier.fillMaxWidth())
                } else {
                    FunnelCard(Modifier.fillMaxWidth()) {
                        Column(
                            Modifier.fillMaxWidth().gap(16.rpx),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            claimedPill()
                            PlanSelector(
                                plans = displays,
                                selectedUuid = selectedUuid,
                                onSelect = { display -> select(display) { selectedUuid = it } }
                            )
                            GuaranteeBadge(Modifier.fillMaxWidth())
                        }
                    }
                }

                Column(Modifier.fillMaxWidth().gap(14.rpx)) {
                    FunnelCopy.TESTIMONIALS.take(3).forEach {
                        TestimonialCard(it, Modifier.fillMaxWidth())
                    }
                }

                if (usingFallback) {
                    SpanText(
                        com.routexk.quiz.i18n.t(
                            "Prices shown are indicative — connect to the backend for live checkout.",
                            "Los precios son indicativos — conéctate al backend para el pago real.",
                        ),
                        Modifier.fontSize(12.rpx).color(com.routexk.quiz.ui.theme.AppColors.Light.TextTertiary)
                    )
                }
            }

            // Sticky CTA: always reachable at the bottom regardless of scroll
            StickyCtaBar {
                FunnelCta(
                    text = FunnelCopy.PAYWALL_CTA,
                    enabled = selectedUuid != null,
                    onClick = {
                        val selected = displays.firstOrNull { it.plan.uuid == selectedUuid }
                        Ga4.event(
                            "paywall_continue_clicked",
                            "plan_id" to selected?.let { Ga4.planId(it.trialDays) },
                            "value" to selected?.plan?.amountCents?.let { it / 100.0 },
                            "currency" to "USD",
                        )
                        if (selected != null) {
                            // Shared event_id: the browser InitiateCheckout and the
                            // backend CAPI copy deduplicate through it. It reaches
                            // the backend via the session's custom_data.
                            val checkoutEventId = FbPixel.newEventId("ic")
                            FbPixel.track(
                                "InitiateCheckout",
                                eventId = checkoutEventId,
                                "content_ids" to FbPixel.ids(Ga4.planId(selected.trialDays)),
                                "content_type" to "product",
                                "value" to selected.plan.amountCents / 100.0,
                                "currency" to "USD",
                                "num_items" to 1,
                            )
                            scope.launch {
                                FunnelApi.saveFbData(
                                    container.defaultHttpClient,
                                    "fb_checkout_event_id" to checkoutEventId,
                                    "fb_checkout_plan_id" to Ga4.planId(selected.trialDays),
                                    "fb_checkout_value" to (selected.plan.amountCents / 100.0).toString(),
                                    "fbp" to FbPixel.fbp(),
                                    "fbc" to FbPixel.fbc(),
                                )
                            }
                        }
                        ctx.router.navigateTo("/checkout")
                    }
                )
            }
        }
    }
}

/** [track] is false for the automatic pre-select on load — GA4 plan_selected means a user tap. */
private fun select(display: PlanDisplay, track: Boolean = true, update: (String) -> Unit) {
    if (track) {
        Ga4.event(
            "plan_selected",
            "plan_id" to Ga4.planId(display.trialDays),
            "value" to display.plan.amountCents / 100.0,
            "currency" to "USD",
        )
        FbPixel.track(
            "AddToCart",
            params = arrayOf(
                "content_ids" to FbPixel.ids(Ga4.planId(display.trialDays)),
                "content_type" to "product",
                "value" to display.plan.amountCents / 100.0,
                "currency" to "USD",
            ),
        )
    }
    FunnelStore.selectedPlan = SelectedPlan(
        uuid = display.plan.uuid,
        label = display.label,
        amountCents = display.plan.amountCents,
        originalCents = display.originalCents,
        trialDays = display.trialDays,
        renewalCents = FunnelCopy.RENEWAL_CENTS,
    )
    update(display.plan.uuid)
}
